import React from 'react';
import { LucideIcon } from 'lucide-react';

type Accent = 'indigo' | 'blue' | 'emerald' | 'violet' | 'amber' | 'red';

const ACCENTS: Record<Accent, { bg: string; border: string; text: string }> = {
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-600' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-600' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-600' },
  violet: { bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-violet-600' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-600' },
  red: { bg: 'bg-red-50', border: 'border-red-100', text: 'text-red-600' },
};

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: React.ReactNode;
  accent?: Accent;
  hint?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, accent = 'indigo', hint, onClick }) => {
  const a = ACCENTS[accent];
  const clickable = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`group text-left bg-white rounded-2xl shadow-sm border border-slate-100 p-5 flex items-center gap-4 transition-all w-full ${
        clickable ? 'hover:shadow-md hover:border-slate-200 hover:-translate-y-0.5 cursor-pointer' : 'cursor-default'
      }`}
    >
      <div className={`p-3 ${a.bg} rounded-xl border ${a.border} flex-shrink-0`}>
        <Icon className={`h-6 w-6 ${a.text}`} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest truncate">{label}</p>
        <p className="text-2xl font-extrabold text-slate-900 mt-0.5 leading-tight">{value}</p>
        {hint && <p className="text-xs text-slate-400 mt-0.5 truncate">{hint}</p>}
      </div>
    </button>
  );
};

export default StatCard;
